import __request__ from 'routing/request';
interface RetentionStrategyConfig {
    readonly max_number_of_indices: number;
    readonly type: string;
}
interface IndexSetFieldRestriction {
    readonly type: string;
}
interface RuleBuilderStep {
    readonly step_title: string;
    readonly negate: boolean;
    readonly function: string;
    readonly id: string;
    readonly params: {
        readonly [_key: string]: Object;
    };
    readonly outputvariable: string;
    readonly conditions: RuleBuilderStep[];
    readonly operator: 'AND' | 'OR';
    readonly errors: string[];
}
interface DataTieringConfig {
    readonly index_lifetime_min: string;
    readonly index_lifetime_max: string;
    readonly type: string;
}
interface RuleBuilderSimulatorResponse {
    readonly processing_time: string;
    readonly source_input_id: string;
    readonly sequence_nr: number;
    readonly field_names: string[];
    readonly fields_entries: unknown[];
    readonly journal_offset: number;
    readonly source: string;
    readonly inet_address: string;
    readonly simulator_action_variables: unknown[];
    readonly simulator_condition_variables: {
        readonly [_key: string]: Object;
    };
    readonly index_sets: IndexSet[];
    readonly id: string;
    readonly timestamp: string;
    readonly streams: Stream[];
    readonly is_source_inet_address: boolean;
    readonly receive_time: string;
    readonly message_id: string;
    readonly message: string;
    readonly message_queue_id: Object;
    readonly filter_out: boolean;
    readonly size: number;
    readonly stream_ids: string[];
    readonly field_count: number;
    readonly fields: {
        readonly [_key: string]: Object;
    };
    readonly complete: boolean;
}
interface StreamRule {
    readonly field: string;
    readonly stream_id: string;
    readonly description: string;
    readonly id: string;
    readonly validations: {
        readonly [_key: string]: Validator;
    };
    readonly inverted: boolean;
    readonly type: 'EXACT' | 'REGEX' | 'GREATER' | 'SMALLER' | 'PRESENCE' | 'CONTAINS' | 'ALWAYS_MATCH' | 'MATCH_INPUT';
    readonly fields: {
        readonly [_key: string]: Object;
    };
    readonly content_pack: string;
    readonly value: string;
}
interface IndexSetConfig {
    readonly index_optimization_max_num_segments: number;
    readonly description: string;
    readonly index_optimization_disabled: boolean;
    readonly _scope: string;
    readonly field_type_profile: string;
    readonly title: string;
    readonly writable: boolean;
    readonly custom_field_mappings: CustomFieldMapping[];
    readonly index_analyzer: string;
    readonly rotation_strategy: RotationStrategyConfig;
    readonly index_template_type: string;
    readonly id: string;
    readonly regular: boolean;
    readonly index_match_pattern: string;
    readonly index_template_name: string;
    readonly replicas: number;
    readonly field_type_refresh_interval: string;
    readonly creation_date: string;
    readonly index_wildcard: string;
    readonly retention_strategy_class: string;
    readonly retention_strategy: RetentionStrategyConfig;
    readonly shards: number;
    readonly field_restrictions: {
        readonly [_key: string]: IndexSetFieldRestriction[];
    };
    readonly rotation_strategy_class: string;
    readonly index_prefix: string;
    readonly data_tiering: DataTieringConfig;
}
interface RotationStrategyConfig {
    readonly type: string;
}
type anyArray = unknown[];
interface CustomFieldMapping {
    readonly field: string;
    readonly type: string;
}
interface SimulateRuleBuilderRequest {
    readonly message: string;
    readonly rule_builder_dto: RuleBuilderDto;
}
interface RuleBuilderDto {
    readonly simulator_message: string;
    readonly rule_builder: RuleBuilder;
    readonly description: string;
    readonly created_at: string;
    readonly id: string;
    readonly source: string;
    readonly title: string;
    readonly modified_at: string;
}
interface Output {
    readonly creator_user_id: string;
    readonly configuration: {
        readonly [_key: string]: Object;
    };
    readonly created_at: string;
    readonly id: string;
    readonly type: string;
    readonly title: string;
    readonly content_pack: string;
}
interface Stream {
    readonly outputs: Output[];
    readonly creator_user_id: string;
    readonly paused: boolean;
    readonly editable: boolean;
    readonly created_at: string;
    readonly description: string;
    readonly title: string;
    readonly output_ids: unknown[];
    readonly stream_rules: StreamRule[];
    readonly content_pack: string;
    readonly index_set_id: string;
    readonly index_set: IndexSet;
    readonly matching_type: 'AND' | 'OR';
    readonly remove_matches_from_default_stream: boolean;
    readonly scope: string;
    readonly fingerprint: number;
    readonly disabled: boolean;
    readonly categories: string[];
    readonly id: string;
    readonly default_stream: boolean;
}
interface RuleBuilder {
    readonly conditions: RuleBuilderStep[];
    readonly actions: RuleBuilderStep[];
    readonly operator: 'AND' | 'OR';
    readonly errors: string[];
}
interface Validator {
}
interface IndexSet {
    readonly write_index_alias: string;
    readonly managed_indices: string[];
    readonly active_write_index: string;
    readonly all_index_aliases: {
        readonly [_key: string]: string[];
    };
    readonly up: boolean;
    readonly index_wildcard: string;
    readonly index_prefix: string;
    readonly newest_index: string;
    readonly config: IndexSetConfig;
}
/**
 * Create a processing rule from rule builder
 */
export function createFromBuilder(rule: RuleBuilderDto): Promise<RuleBuilderDto> {
    return __request__('POST', '/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder', rule, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get action descriptors for rule builder
 */
export function actions(): Promise<anyArray> {
    return __request__('GET', '/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder/actions', null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get condition descriptors for ruleBuilder
 */
export function conditions(): Promise<anyArray> {
    return __request__('GET', '/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder/conditions', null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Simulate a single processing rule created by the rule builder
 */
export function simulate(rule: SimulateRuleBuilderRequest): Promise<RuleBuilderSimulatorResponse> {
    return __request__('POST', '/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder/simulate', rule, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Validate rule builder
 */
export function validate(rule: RuleBuilderDto): Promise<RuleBuilderDto> {
    return __request__('POST', '/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder/validate', rule, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Update a processing rule from rule builder
 */
export function updateFromBuilder(rule: RuleBuilderDto, id?: string): Promise<RuleBuilderDto> {
    return __request__('PUT', `/plugins/org.graylog.plugins.pipelineprocessor/system/pipelines/rulebuilder/${id}`, rule, {}, {
        'Accept': ['application/json']
    });
}
